import React, { useState } from "react";
import { Ruler } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useStore } from "@/context/StoreContext";
import { postSizeFinder } from "@/lib/api";

const FITS = [
  { id: "slim", label: "Slim" },
  { id: "regular", label: "True to size" },
  { id: "relaxed", label: "Oversized" },
];

export const SizeFinder = () => {
  const { sizeFinderOpen, setSizeFinderOpen } = useStore();
  const [height, setHeight] = useState(175);
  const [weight, setWeight] = useState(72);
  const [fit, setFit] = useState("regular");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const find = async () => {
    setLoading(true);
    try {
      const r = await postSizeFinder({ height_cm: Number(height), weight_kg: Number(weight), fit });
      setResult(r);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={sizeFinderOpen} onOpenChange={(o) => { setSizeFinderOpen(o); if (!o) setResult(null); }}>
      <DialogContent data-testid="size-finder-dialog" className="bg-[#F4F2ED] border border-black/10 sm:max-w-md p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2 font-display uppercase tracking-widest text-sm">
            <Ruler size={16} /> Smart Size Finder
          </DialogTitle>
          <DialogDescription className="sr-only">Find your recommended size</DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6 pt-2 space-y-6">
          <p className="font-body text-sm text-black/60">Two numbers. Zero guesswork. Fewer returns.</p>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="overline text-black/50">Height (cm)</span>
              <input data-testid="size-height" type="number" value={height} onChange={(e) => setHeight(e.target.value)}
                     className="w-full bg-transparent border-b border-black/30 py-2 outline-none font-mono focus:border-black" />
            </label>
            <label className="block">
              <span className="overline text-black/50">Weight (kg)</span>
              <input data-testid="size-weight" type="number" value={weight} onChange={(e) => setWeight(e.target.value)}
                     className="w-full bg-transparent border-b border-black/30 py-2 outline-none font-mono focus:border-black" />
            </label>
          </div>
          <div>
            <span className="overline text-black/50">Preferred fit</span>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {FITS.map((f) => (
                <button key={f.id} data-testid={`fit-${f.id}`} onClick={() => setFit(f.id)}
                        className={`py-2.5 overline border transition-colors ${fit === f.id ? "bg-[#0A0A0A] text-[#F4F2ED] border-black" : "border-black/20 hover:border-black"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {result ? (
            <div data-testid="size-result" className="bg-[#0A0A0A] text-[#F4F2ED] p-6 text-center">
              <p className="overline text-white/50">Your size</p>
              <div className="font-hero text-6xl my-2 text-[#FFFFFF]">{result.recommended}</div>
              <p className="font-body text-sm text-white/70">{result.note}</p>
              <p className="font-mono text-[11px] text-white/40 mt-3">Between sizes? Try {result.alternate}.</p>
              <button onClick={() => setResult(null)} className="btn-ghost-light mt-5 w-full">Recalculate</button>
            </div>
          ) : (
            <button onClick={find} disabled={loading} data-testid="size-find-btn" className="btn-fill w-full disabled:opacity-60">
              {loading ? "Finding…" : "Find My Size"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
